import React, { useEffect, useState } from "react";
import axios from "axios";
import DataTable, { TableColumn } from "react-data-table-component";
import * as XLSX from "sheetjs-style";
import { useDispatch, useSelector } from "react-redux";
import { updateDashboardFields } from "../../../features/common/commonSlice";
import { RootState } from "../../../store";
const FaFileExcel = require("react-icons/fa").FaFileExcel;

interface MeetingData {
  id: number;
  name: string;
  total_gps: number;
  gps_with_mom_generated: number;
  total_meetings_uploaded: number;
  draft_mom: number;
  final_submitted_mom: number;
  type: "ZP" | "BP" | "GP";
}

const GramDashboard2: React.FC<{ finYear: string; meetingType: any }> = ({ finYear, meetingType }) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const dispatch = useDispatch();
  const [tableData, setTableData] = useState<MeetingData[]>([]);
  const [filterText, setFilterText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isDirectGP, setIsDirectGP] = useState(false);

  const gramData = useSelector((state: RootState) => state.common.dashboardData["Gram Panchayats"]);
  const isBpDirectState = Number(gramData?.state_code) === 34;
  const nameLabel = isDirectGP ? "GP" : isBpDirectState ? "BP" : "ZP";

  useEffect(() => {
    const fetchGramAggregates = async () => {
      setTableData([]);
      if (!gramData?.state_code) return;
      try {
        const response = await axios.get(`${baseUrl}api/gp_report/${gramData.state_code}`, {
          params: { fin_year: finYear, meeting_type: meetingType?.value !== "0" ? meetingType.value : undefined },
        });
        const apiData = response?.data?.data || [];
        const isGP = apiData.length && "gp_code" in apiData[0];
        setIsDirectGP(isGP);

        const mappedData: MeetingData[] = apiData.map((item: any) => ({
          id: item.gp_code || item.zp_lgd_code || item.bp_lgd_code,
          name: item.gp_name || item.zp_name || item.bp_name,
          total_gps: item.total_gps || 1,
          gps_with_mom_generated: item.gps_with_mom_generated || (item.total_meetings_uploaded > 0 ? 1 : 0),
          total_meetings_uploaded: item.total_meetings_uploaded || 0,
          draft_mom: item.draft_mom || 0,
          final_submitted_mom: item.final_submitted_mom || 0,
          type: isGP ? "GP" : (isBpDirectState ? "BP" : "ZP"),
        }));
        setTableData(mappedData);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchGramAggregates();
  }, [finYear, meetingType, baseUrl, gramData?.state_code]);

  const handleMomHighlight = (id: number, name: string, type: string) => {
    const fields = type === "ZP" ? { zp_lgd_code: id, zp_name: name } : 
                   type === "BP" ? { bp_lgd_code: id, bp_name: name, zp_lgd_code: null, zp_name: "" } : 
                   { gp_lgd_code: id, gp_name: name, zp_lgd_code: null, zp_name: "", bp_lgd_code: null, bp_name: "" };
    dispatch(updateDashboardFields({ menu: "Gram Panchayats", fields }));
  };

  const handleBack = () => {
    dispatch(updateDashboardFields({ menu: "Gram Panchayats", fields: { state_code: null, state_name: "", zp_lgd_code: null, zp_name: "", bp_lgd_code: null, bp_name: "" } }));
  };

  const columns: TableColumn<MeetingData>[] = [
    { name: "S.No.", cell: (_row, i) => (i + 1 + (currentPage - 1) * rowsPerPage).toString(), width: "80px" },
    {
      name: `${nameLabel} Name`,
      selector: (r) => r.name,
      cell: (r) => (
        <span className={r.total_meetings_uploaded > 0 ? "highlight-td" : ""}
          style={{ cursor: r.total_meetings_uploaded > 0 ? "pointer" : "default" }}
          onClick={() => r.total_meetings_uploaded > 0 && handleMomHighlight(r.id, r.name, r.type)}>
          {r.name}
        </span>
      ), sortable: true,
    },
    { name: "Total GPs", selector: (r) => r.total_gps, sortable: true, omit: isDirectGP },
    { name: "GPs with MoM Generated", selector: (r) => r.gps_with_mom_generated, sortable: true, omit: isDirectGP },
    { name: "Draft MoM", selector: (r) => r.draft_mom, sortable: true },
    { name: "Final Submitted", selector: (r) => r.final_submitted_mom, sortable: true },
    { name: "Total Meetings Uploaded", selector: (r) => r.total_meetings_uploaded, sortable: true },
  ];

  const filteredData = tableData.filter((i) => i.name?.toLowerCase().includes(filterText.toLowerCase()) || String(i.id).includes(filterText));

  const t = {
    gps: filteredData.reduce((s, r) => s + r.total_gps, 0),
    gen: filteredData.reduce((s, r) => s + r.gps_with_mom_generated, 0),
    draft: filteredData.reduce((s, r) => s + r.draft_mom, 0),
    final: filteredData.reduce((s, r) => s + r.final_submitted_mom, 0),
    upld: filteredData.reduce((s, r) => s + r.total_meetings_uploaded, 0),
  };

  const exportExcel = () => {
    const header = ["S.No.", `${nameLabel} Name`, "Total GPs", "GPs with MoM Generated", "Draft MoM", "Final Submitted", "Total Meetings Uploaded"];
    const data = filteredData.map((r, i) => [i + 1, r.name, r.total_gps, r.gps_with_mom_generated, r.draft_mom, r.final_submitted_mom, r.total_meetings_uploaded]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...data, ["", "Total", t.gps, t.gen, t.draft, t.final, t.upld]]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "GramReport");
    XLSX.writeFile(wb, "GramReport.xlsx");
  };

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between mb-2">
        <div className="d-flex">
          <button className="back-btn me-2" onClick={handleBack}>&larr; Back</button>
          <div className="data-chip me-2">{gramData?.state_name} - {gramData?.state_code}</div>
        </div>
        <button className="shiny-btn" onClick={exportExcel}><FaFileExcel /> Export Excel</button>
      </div>
      <input type="text" className="form-control w-25 mb-2" placeholder="Search..." value={filterText} onChange={(e) => setFilterText(e.target.value)} />
      <DataTable columns={columns} data={filteredData} pagination onChangePage={setCurrentPage} onChangeRowsPerPage={setRowsPerPage} highlightOnHover striped />
      {filteredData.length > 0 && (
        <div className="table-footer fw-bold bg-light d-flex p-2 border">
          <div style={{ width: "80px" }}></div>
          <div style={{ flex: 1 }}>Total</div>
          {!isDirectGP && <><div style={{ flex: 1 }}>{t.gps}</div><div style={{ flex: 1 }}>{t.gen}</div></>}
          <div style={{ flex: 1 }}>{t.draft}</div>
          <div style={{ flex: 1 }}>{t.final}</div>
          <div style={{ flex: 1 }}>{t.upld}</div>
        </div>
      )}
    </div>
  );
};

export default GramDashboard2;